import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import Papa from 'papaparse'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { capabilities } from '@homevest/utils'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  ListItem,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  UnorderedList,
  Wrap,
  WrapItem
} from '@chakra-ui/react'

import axios from 'lib/axios'
import { hasCapability } from 'lib/admin-perms'
import { useRentRollQuery, useUpup_LiabilityTypesQuery } from 'graphql/generated'

import { prepareRows, rowAddress, rowTenantName } from './resolve'
import { isAllowedLiabilityType } from 'lib/liability-types'
import {
  DryRunResponse,
  DryRunRow,
  ParsedCsvRow,
  PreparedLiabilityRow,
  SubmitResult
} from './types'

const { LEDGER_LIABILITY_CREATOR, LEDGER_SUPER_EDITOR, LEDGER_VIEWER } =
  capabilities.CAPABILITY_TYPES

const PREVIEW_TABLE_MAX_WIDTH = '8xl'

const FORMAT_SECTIONS = [
  {
    columns: ['rental_id', 'address', 'occupancy_date', 'tenant_name'],
    description: 'Use a rental ID, or use an address plus a move-in date or tenant name.',
    title: 'Find the rental'
  },
  {
    columns: ['amount', 'date'],
    description:
      'Amounts must be greater than 0. Dates can use MM/DD/YY, MM/DD/YYYY, or YYYY-MM-DD.',
    title: 'Enter the charge'
  },
  {
    columns: ['liability_type'],
    description: 'Put the exact type name in the file, or choose one after upload for blank rows.',
    title: 'Pick the liability type'
  },
  {
    columns: ['note', 'external_note'],
    description:
      'Use note for an internal-facing note (required for active rentals). Use external_note to control what the tenant will see on their Buildium ledger. (By default they will just see the liability type, e.g. Rent or Late Fee.)',
    title: 'Add notes'
  }
]

const normalizeHeader = (header: string) => header.trim().toLowerCase()

const stringifyError = (error: any) =>
  error?.message || error?.error || error?.data?.message || 'Unable to complete request'

const toDryRunRow = (row: PreparedLiabilityRow): DryRunRow => ({
  amount: row.amount!,
  date: row.date!,
  external_note: row.externalNote,
  liability_type_id: row.liabilityTypeId!,
  note: row.note,
  rental_id: row.rentalId!
})

export default function BulkLiabilities() {
  const admin = useSelector<any>((state) => state.admin)
  const canUsePage =
    hasCapability(admin as any, LEDGER_LIABILITY_CREATOR) &&
    hasCapability(admin as any, LEDGER_SUPER_EDITOR) &&
    hasCapability(admin as any, LEDGER_VIEWER)

  const [{ data: rentRollData, fetching: isRentRollLoading }] = useRentRollQuery({
    pause: !canUsePage
  })
  const [{ data: liabilityTypesData, fetching: isLiabilityTypesLoading }] =
    useUpup_LiabilityTypesQuery({
      pause: !canUsePage
    })

  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [defaultLiabilityTypeId, setDefaultLiabilityTypeId] = useState('')
  const [dryRunErrorsByIndex, setDryRunErrorsByIndex] = useState<Record<number, string>>({})
  const [isDryRunLoading, setIsDryRunLoading] = useState(false)
  const [isSubmitConfirmed, setIsSubmitConfirmed] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [parseError, setParseError] = useState('')
  const [parsedRows, setParsedRows] = useState<ParsedCsvRow[]>([])
  const [submitResults, setSubmitResults] = useState<SubmitResult[]>([])
  const [uploadRevision, setUploadRevision] = useState(0)
  const submitStatusRef = useRef<HTMLDivElement | null>(null)

  const rentRollRows = useMemo(() => rentRollData?.rent_roll ?? [], [rentRollData])
  const liabilityTypes = useMemo(
    () => liabilityTypesData?.liability_types ?? [],
    [liabilityTypesData]
  )
  const selectableLiabilityTypes = useMemo(
    () =>
      liabilityTypes
        .filter(isAllowedLiabilityType)
        .sort((left, right) => left.name.localeCompare(right.name)),
    [liabilityTypes]
  )

  const preparedRows = useMemo(
    () =>
      prepareRows({
        defaultLiabilityTypeId,
        liabilityTypes,
        parsedRows,
        rentRollRows
      }),
    [defaultLiabilityTypeId, liabilityTypes, parsedRows, rentRollRows]
  )
  const hasUploadedCsv = csvHeaders.length > 0 || parsedRows.length > 0
  const hasLiabilityTypeColumn = csvHeaders.includes('liability_type')
  const needsFallbackLiabilityType =
    hasUploadedCsv &&
    (!hasLiabilityTypeColumn || parsedRows.some((row) => !row.raw.liability_type?.trim()))

  const dryRunCandidates = useMemo(
    () =>
      preparedRows.filter(
        (row) =>
          row.errors.length === 0 &&
          row.amount !== undefined &&
          row.date &&
          row.liabilityTypeId &&
          row.rentalId
      ),
    [preparedRows]
  )
  const dryRunPayload = useMemo(() => dryRunCandidates.map(toDryRunRow), [dryRunCandidates])
  const dryRunPayloadKey = useMemo(
    () => JSON.stringify({ rows: dryRunPayload, uploadRevision }),
    [dryRunPayload, uploadRevision]
  )

  useEffect(() => {
    setDryRunErrorsByIndex({})

    if (dryRunPayload.length === 0) {
      setIsDryRunLoading(false)
      return
    }

    let isCurrent = true
    setIsDryRunLoading(true)

    axios
      .post<DryRunResponse>('/admin/rental_liabilities/dry_run', dryRunPayload)
      .then(({ data }) => {
        if (!isCurrent) {
          return
        }

        const nextErrors: Record<number, string> = {}
        for (const dryRunRow of data.rows) {
          if (dryRunRow.error) {
            nextErrors[dryRunCandidates[dryRunRow.index].index] = dryRunRow.error
          }
        }
        setDryRunErrorsByIndex(nextErrors)
      })
      .catch((error) => {
        if (!isCurrent) {
          return
        }

        const message = stringifyError(error)
        const nextErrors: Record<number, string> = {}
        for (const row of dryRunCandidates) {
          nextErrors[row.index] = message
        }
        setDryRunErrorsByIndex(nextErrors)
      })
      .finally(() => {
        if (isCurrent) {
          setIsDryRunLoading(false)
        }
      })

    return () => {
      isCurrent = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dryRunPayloadKey])

  const rowsWithErrors = preparedRows.map((row) => ({
    ...row,
    allErrors: [...row.errors, dryRunErrorsByIndex[row.index]].filter(Boolean) as string[]
  }))
  const invalidRows = rowsWithErrors.filter((row) => row.allErrors.length > 0)
  const validRows = rowsWithErrors.filter((row) => row.allErrors.length === 0)

  useEffect(() => {
    if (!isSubmitting && submitResults.length === 0) {
      return
    }

    const timeout = window.setTimeout(() => {
      submitStatusRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })

    return () => window.clearTimeout(timeout)
  }, [isSubmitting, submitResults.length])

  const onDrop = useCallback((files: File[]) => {
    const file = files[0]
    setDefaultLiabilityTypeId('')
    setDryRunErrorsByIndex({})
    setIsSubmitConfirmed(false)
    setParseError('')
    setParsedRows([])
    setSubmitResults([])

    if (!file) {
      return
    }

    setUploadRevision((revision) => revision + 1)

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      transform: (value) => (typeof value === 'string' ? value.trim() : value),
      transformHeader: normalizeHeader,
      complete: (result) => {
        const fields = result.meta.fields ?? []
        setCsvHeaders(fields)
        setParsedRows(
          result.data.map((row, index) => ({
            index,
            raw: row,
            rowNumber: index + 2
          }))
        )
      },
      error: (error) => {
        setParseError(error.message)
      }
    })
  }, [])

  const { getInputProps, getRootProps, isDragActive } = useDropzone({
    accept: '.csv,text/csv',
    multiple: false,
    onDrop
  })

  const canSubmit =
    parsedRows.length > 0 &&
    invalidRows.length === 0 &&
    validRows.length > 0 &&
    !isDryRunLoading &&
    !isSubmitting &&
    isSubmitConfirmed

  const submit = async () => {
    if (!canSubmit) {
      return
    }

    setIsSubmitting(true)
    setSubmitResults([])

    const rowsByRentalId = new Map<string, PreparedLiabilityRow[]>()
    for (const row of validRows) {
      rowsByRentalId.set(row.rentalId!, [...(rowsByRentalId.get(row.rentalId!) ?? []), row])
    }

    const nextResults: SubmitResult[] = []
    for (const [rentalId, rows] of rowsByRentalId.entries()) {
      try {
        await axios.post('/admin/rental_liabilities/bulk', {
          liabilities: rows.map(toDryRunRow)
        })
        nextResults.push({
          count: rows.length,
          rentalId,
          rows,
          status: 'success'
        })
      } catch (error) {
        nextResults.push({
          count: rows.length,
          error: stringifyError(error),
          rentalId,
          rows,
          status: 'failure'
        })
      }
      setSubmitResults([...nextResults])
    }

    setIsSubmitting(false)
    setIsSubmitConfirmed(false)
  }

  const downloadRemaining = () => {
    const failedRows = submitResults
      .filter((result) => result.status === 'failure')
      .flatMap((result) => result.rows)
      .sort((left, right) => left.index - right.index)

    const csv = Papa.unparse({
      fields: csvHeaders,
      data: failedRows.map((row) => row.raw)
    })
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const href = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = href
    link.download = 'remaining-rental-liabilities.csv'
    link.click()
    URL.revokeObjectURL(href)
  }

  if (!canUsePage) {
    return (
      <Alert status='error'>
        <AlertIcon />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You need ledger super editor, ledger liability creator, and ledger viewer permissions.
        </AlertDescription>
      </Alert>
    )
  }

  const isLoading = isRentRollLoading || isLiabilityTypesLoading

  return (
    <Box p={5}>
      <Helmet>
        <title>Bulk Upload Liabilities</title>
      </Helmet>
      <Stack spacing={5}>
        <Heading size='lg' textAlign='center'>
          Bulk Upload Liabilities
        </Heading>
        <Box bg='gray.50' borderColor='teal.500' borderLeft='4px solid' mx='auto' px={5} py={4}>
          <Stack spacing={4}>
            <Box>
              <Heading size='sm'>Before you upload</Heading>
              <Text color='gray.600' fontSize='sm' mt={1}>
                One row creates one liability. A complete file usually only needs a few columns.
              </Text>
            </Box>

            <UnorderedList color='gray.700' fontSize='sm' spacing={4}>
              {FORMAT_SECTIONS.map((section) => (
                <ListItem key={section.title}>
                  <Stack spacing={2}>
                    <Text color='gray.800' fontWeight='semibold'>
                      {section.title}
                    </Text>
                    <Text color='gray.600' fontSize='sm'>
                      {section.description}
                    </Text>
                    <Wrap spacing={2}>
                      {section.columns.map((column) => (
                        <WrapItem key={column}>
                          <Badge colorScheme='gray' px={2} py={1} textTransform='none'>
                            {column}
                          </Badge>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Stack>
                </ListItem>
              ))}
            </UnorderedList>
          </Stack>
        </Box>

        {parseError && (
          <Alert mx='auto' status='error'>
            <AlertIcon />
            <AlertDescription>{parseError}</AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <Spinner alignSelf='center' />
        ) : (
          <>
            <Stack mx='auto' spacing={4}>
              <Box
                {...getRootProps()}
                border='1px dashed'
                borderColor={isDragActive ? 'teal.500' : 'gray.300'}
                borderRadius='md'
                cursor='pointer'
                p={8}
                textAlign='center'
              >
                <input {...getInputProps()} />
                <Text>{isDragActive ? 'Drop CSV here' : 'Choose CSV or drag it here'}</Text>
              </Box>

              {needsFallbackLiabilityType && (
                <FormControl>
                  <FormLabel>Liability type for blank rows</FormLabel>
                  <Select
                    placeholder='Select a liability type'
                    value={defaultLiabilityTypeId}
                    onChange={(event) => setDefaultLiabilityTypeId(event.target.value)}
                  >
                    {selectableLiabilityTypes.map((liabilityType) => (
                      <option key={liabilityType.id} value={liabilityType.id}>
                        {liabilityType.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Stack>

            {hasUploadedCsv && <Divider mx='auto' />}

            {isDryRunLoading && <Spinner />}

            {hasUploadedCsv && (
              <Stack align='stretch' maxW={PREVIEW_TABLE_MAX_WIDTH} mx='auto' spacing={5} w='100%'>
                <PreviewTable
                  colorScheme='red'
                  rows={invalidRows}
                  title={`Invalid Rows (${invalidRows.length})`}
                />
                <PreviewTable
                  colorScheme='teal'
                  rows={validRows}
                  title={`Valid Rows (${validRows.length})`}
                />
              </Stack>
            )}

            {validRows.length > 0 && (
              <Stack align='stretch' mx='auto' px={{ base: 0, md: 4 }} spacing={3}>
                <Checkbox
                  isChecked={isSubmitConfirmed}
                  isDisabled={isSubmitting || invalidRows.length > 0 || isDryRunLoading}
                  onChange={(event) => setIsSubmitConfirmed(event.target.checked)}
                >
                  Upload {validRows.length} liabilities across{' '}
                  {new Set(validRows.map((row) => row.rentalId)).size} rentals?
                </Checkbox>
                <Button
                  colorScheme='teal'
                  isDisabled={!canSubmit}
                  isLoading={isSubmitting}
                  loadingText='Uploading'
                  onClick={submit}
                >
                  Upload
                </Button>
              </Stack>
            )}

            {(isSubmitting || submitResults.length > 0) && (
              <Stack
                ref={submitStatusRef}
                align='stretch'
                maxW={PREVIEW_TABLE_MAX_WIDTH}
                mx='auto'
                px={{ base: 0, md: 4 }}
                spacing={3}
                w='100%'
              >
                {isSubmitting && (
                  <Alert status='info'>
                    <AlertIcon />
                    <AlertDescription>
                      Uploading liabilities. Results will appear here as each rental finishes.
                    </AlertDescription>
                  </Alert>
                )}

                {submitResults.length > 0 && (
                  <>
                    <Heading size='md'>Result</Heading>
                    <TableContainer w='100%'>
                      <Table size='sm' variant='striped' w='100%'>
                        <Thead>
                          <Tr>
                            <Th>Rental ID</Th>
                            <Th>Status</Th>
                            <Th isNumeric>Rows</Th>
                            <Th>Error</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {submitResults.map((result) => (
                            <Tr key={result.rentalId}>
                              <Td>{result.rentalId}</Td>
                              <Td>
                                <Badge colorScheme={result.status === 'success' ? 'green' : 'red'}>
                                  {result.status}
                                </Badge>
                              </Td>
                              <Td isNumeric>{result.count}</Td>
                              <Td>{result.error}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </TableContainer>
                    {submitResults.some((result) => result.status === 'failure') && (
                      <Stack>
                        <Alert status='warning'>
                          <AlertIcon />
                          <AlertDescription>
                            Successful rentals are already saved. Re-uploading the remaining CSV
                            will not create duplicates because the dry-run will catch any that did
                            land.
                          </AlertDescription>
                        </Alert>
                        <Button alignSelf='flex-start' onClick={downloadRemaining}>
                          Download remaining
                        </Button>
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Box>
  )
}

function PreviewTable({
  colorScheme,
  rows,
  title
}: {
  colorScheme: string
  rows: Array<PreparedLiabilityRow & { allErrors: string[] }>
  title: string
}) {
  return (
    <Stack align='stretch' px={{ base: 0, md: 4 }} spacing={2} w='100%'>
      <Heading size='md'>{title}</Heading>
      <TableContainer w='100%'>
        <Table size='sm' variant='striped' colorScheme={colorScheme} w='100%'>
          <Thead>
            <Tr>
              <Th>CSV Row</Th>
              <Th>Rental ID</Th>
              <Th>Address</Th>
              <Th>Tenant</Th>
              <Th>Type</Th>
              <Th isNumeric>Amount</Th>
              <Th>Date</Th>
              <Th>Note</Th>
              <Th>External Note</Th>
              <Th>Errors</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => (
              <Tr key={row.index}>
                <Td>{row.rowNumber}</Td>
                <Td>{row.rentalId}</Td>
                <Td>{rowAddress(row.rental)}</Td>
                <Td>{row.rental ? rowTenantName(row.rental) : ''}</Td>
                <Td>{row.liabilityType?.name ?? row.liabilityTypeId}</Td>
                <Td isNumeric>{row.amount}</Td>
                <Td>{row.date}</Td>
                <Td whiteSpace='normal'>{row.note}</Td>
                <Td whiteSpace='normal'>{row.externalNote}</Td>
                <Td>{row.allErrors.join(' ')}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  )
}
